
(() => {
  "use strict";

  const root = document.querySelector(
    "[data-helpster-cart-details]"
  );

  if (!root) return;

  const content = root.querySelector(
    "[data-helpster-cart-details-content]"
  );

  if (!content) return;

  const shopRoot =
    window.Shopify?.routes?.root || "/";

  const proxyBase =
    shopRoot + "apps/helpster-addresses";

  function element(tag, className, text) {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (text !== undefined && text !== null) {
      node.textContent = String(text);
    }

    return node;
  }

  function showMessage(target, message, isError = false) {
    target.replaceChildren(
      element(
        "p",
        isError
          ? "helpster-cart-details__error"
          : "helpster-cart-details__notice",
        message
      )
    );
  }

  async function readJson(response) {
    const result = await response.json();

    if (!response.ok || result.success !== true) {
      const error = new Error(
        result.message ||
        "Palvelutietojen käsittely epäonnistui."
      );

      error.code = result.error || "request_failed";
      error.httpStatus = response.status;

      throw error;
    }

    return result;
  }

  async function getDraft(draftId) {
    const url = new URL(
      proxyBase,
      location.origin
    );

    url.searchParams.set(
      "action",
      "getServiceDraft"
    );

    url.searchParams.set(
      "draftId",
      draftId
    );

    const response = await fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });

    const result = await readJson(response);

    if (
      !result.draft ||
      result.draft.draftId !== draftId
    ) {
      throw new Error(
        "Palveluluonnoksen tunniste ei täsmää."
      );
    }

    return result.draft;
  }

  async function updateDraft({
    draftId,
    expectedRevision,
    changes,
  }) {
    const url = new URL(
      proxyBase,
      location.origin
    );

    url.searchParams.set(
      "action",
      "updateServiceDraft"
    );

    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        draftId,
        expectedRevision,
        changes,
      }),
    });

    return readJson(response);
  }

  async function readHelpsterCart() {
  const response = await fetch(
    shopRoot + "cart.js",
    {
      credentials: "same-origin",
      cache: "no-store",
    }
  );

  if (!response.ok) {
    throw new Error(
      "Ostoskorin tietoja ei voitu lukea."
    );
  }

  const cart = await response.json();

  if (
    cart.currency !== "EUR" ||
    !Array.isArray(cart.items)
  ) {
    throw new Error(
      "Ostoskorin valuutta tai rakenne on virheellinen."
    );
  }

  return cart;
}

/*
 * Hae luonnoksen ajantasainen hintatarjous.
 */
async function getHelpsterDraftQuote(draftId) {
  const url = new URL(
    proxyBase,
    location.origin
  );

  url.searchParams.set(
    "action",
    "quoteServiceDraft"
  );

  url.searchParams.set(
    "draftId",
    draftId
  );

  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      Accept: "application/json",
    }
  });

  const result = await readJson(response);
  const quote = result.quote;

  if (
    !quote ||
    quote.draftId !== draftId ||
    quote.serviceKey !== "general_cleaning" ||
    quote.currency !== "EUR" ||
    quote.pricingVersion !== "example-v1" ||
    !Number.isSafeInteger(quote.revision) ||
    quote.revision < 0 ||
    !Number.isSafeInteger(quote.finalPriceCents) ||
    quote.finalPriceCents < 1000 ||
    quote.finalPriceCents % 500 !== 0 ||
    !/^[1-9]\d+$/.test(
      String(quote.shopify?.variantId ?? "")
    ) ||
    quote.shopify?.variantPriceCents !==
      quote.finalPriceCents ||
    quote.shopify?.quantity !== 1 ||
    quote.shopify?.currency !== "EUR" ||
    quote.quantity !== 1 ||
    quote.checkoutReady !== false
  ) {
    throw new Error(
      "Firebase palautti virheellisen hintatarjouksen."
    );
  }

  return quote;
}

/*
 * Etsi täsmälleen yksi palveluluonnoksen rivi.
 */
function findHelpsterDraftLine(cart, draftId) {
  const matches = cart.items.filter(
    item =>
      item.properties?._helpster_draft_id === draftId &&
      item.properties?._helpster_service_key ===
        "general_cleaning"
  );

  if (matches.length !== 1) {
    throw new Error(
      "Palvelulle ei löytynyt täsmälleen yhtä " +
      "ostoskoririviä. Tarkista ostoskori."
    );
  }

  const line = matches[0];

  if (
    !line.key ||
    line.quantity !== 1
  ) {
    throw new Error(
      "Palvelun ostoskoririvi on virheellinen."
    );
  }

  return line;
}

/*
 * Tee Shopify Cart -muutos.
 *
 * Tätä käytetään vain ostoskoririvien
 * lisäämiseen ja poistamiseen.
 */
async function postHelpsterCart(endpoint, payload) {
  const response = await fetch(
    shopRoot + endpoint,
    {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  let result;

  try {
    result = await response.json();
  } catch {
    throw new Error(
      "Shopify ei palauttanut kelvollista vastausta."
    );
  }

  if (!response.ok) {
    throw new Error(
      result.description ||
      result.message ||
      "Shopify-ostoskorin päivittäminen epäonnistui."
    );
  }

  return result;
}

async function syncHelpsterCartPrice(draftId) {
  const quote =
    await getHelpsterDraftQuote(draftId);

  const beforeCart =
    await readHelpsterCart();

  const oldLine =
    findHelpsterDraftLine(
      beforeCart,
      draftId
    );

  const expectedVariantId =
    String(quote.shopify.variantId);

  const expectedPrice =
    quote.finalPriceCents;

  /*
   * Jos variantti ja hinta ovat jo oikein,
   * ei tehdä turhia Cart API -pyyntöjä.
   */
  if (
    String(oldLine.variant_id) ===
      expectedVariantId &&
    oldLine.price === expectedPrice
  ) {
    return {
      changed: false,
      priceCents: expectedPrice,
      revision: quote.revision,
    };
  }

  /*
   * Ennen vaihtoa varmistetaan, että
   * luonnoksen revisio ei ole muuttunut
   * hintatarjouksen hakemisen jälkeen.
   */
  const latestDraft =
    await getDraft(draftId);

  if (
    latestDraft.revision !== quote.revision
  ) {
    throw new Error(
      "Palvelun tietoja muutettiin hinnanlaskennan " +
      "aikana. Yritä hinnanpäivitystä uudelleen."
    );
  }

  /*
   * Säilytetään nykyiset rivin ominaisuudet.
   * Tämä on tärkeää draftId:n ja muiden
   * mahdollisten metatietojen vuoksi.
   */
  const properties = {
    ...oldLine.properties,
  };

  /*
   * Lisätään ensin uusi variantti.
   *
   * Jos tämä epäonnistuu, vanha rivi
   * jää vielä ostoskoriin.
   */
  await postHelpsterCart(
    "cart/add.js",
    {
      items: [
        {
          id: expectedVariantId,
          quantity: 1,
          properties,
        },
      ],
    }
  );

  /*
   * Varmistetaan, että uusi variantti
   * on todella lisätty oikein.
   */
  const afterAdd =
    await readHelpsterCart();

  const newLines = afterAdd.items.filter(
    line =>
      line.properties?._helpster_draft_id ===
        draftId &&
      String(line.variant_id) ===
        expectedVariantId
  );

  if (
    newLines.length !== 1 ||
    newLines[0].quantity !== 1 ||
    newLines[0].price !== expectedPrice
  ) {
    /*
     * Emme poista vanhaa riviä, jos uuden
     * variantin oikeellisuutta ei voida
     * varmistaa.
     */
    throw new Error(
      "Uuden Shopify-variantin hinta ei täsmää. " +
      "Vanha palvelurivi säilytettiin. " +
      "Tarkista ostoskori."
    );
  }

  /*
   * Poistetaan alkuperäinen variantti
   * juuri sen yksilöllisen key-tunnisteen
   * perusteella.
   */
  try {
    await postHelpsterCart(
      "cart/change.js",
      {
        id: oldLine.key,
        quantity: 0,
      }
    );
  } catch (error) {
    /*
     * Yritetään perua juuri lisätyn
     * variantin lisääminen.
     *
     * Jos palautus epäonnistuu, käyttäjän
     * on tarkistettava ostoskori.
     */
    try {
      await postHelpsterCart(
        "cart/change.js",
        {
          id: newLines[0].key,
          quantity: 0,
        }
      );
    } catch (rollbackError) {
      console.error(
        "[Helpster] Hintapäivityksen palautus epäonnistui:",
        rollbackError
      );
    }

    throw new Error(
      "Vanhan hintavariantin poistaminen " +
      "epäonnistui. Tarkista ostoskori. " +
      "Alkuperäinen virhe: " +
      (error.message || "tuntematon virhe")
    );
  }

  /*
   * Varmistetaan lopullinen ostoskoririvi.
   */
  const finalCart =
    await readHelpsterCart();

  const finalLine =
    findHelpsterDraftLine(
      finalCart,
      draftId
    );

  if (
    String(finalLine.variant_id) !==
      expectedVariantId ||
    finalLine.price !== expectedPrice ||
    finalLine.quantity !== 1
  ) {
    throw new Error(
      "Ostoskorin lopullinen hinta tai variantti " +
      "ei täsmää. Tarkista ostoskori."
    );
  }

  /*
   * Tarkistetaan vielä, ettei luonnos
   * muuttunut variantin vaihdon aikana.
   */
  const finalDraft =
    await getDraft(draftId);

  if (
    finalDraft.revision !== quote.revision
  ) {
    throw new Error(
      "Palvelun tiedot muuttuivat hinnanpäivityksen " +
      "aikana. Hinta täytyy tarkistaa uudelleen."
    );
  }

  return {
    changed: true,
    priceCents: expectedPrice,
    revision: quote.revision,
  };
}

  function detailRow(label, value) {
    const row = element(
      "div",
      "helpster-cart-details__row"
    );

    row.append(
      element(
        "span",
        "helpster-cart-details__label",
        label
      ),
      element(
        "span",
        "helpster-cart-details__value",
        value
      )
    );

    return row;
  }

  function renderSummary(target, draft) {
    const box = element(
      "div",
      "helpster-cart-details__information"
    );

    const rows = draft.display?.rows || [];

    for (const row of rows) {
      if (
        typeof row.label !== "string" ||
        typeof row.value !== "string"
      ) {
        continue;
      }

      box.appendChild(
        detailRow(row.label, row.value)
      );
    }

    if (draft.display?.complete !== true) {
      box.appendChild(
        element(
          "p",
          "helpster-cart-details__notice",
          "Osa palvelun tiedoista vaatii tarkistuksen."
        )
      );
    }

    const instructions =
      typeof draft.customerInstructions === "string"
        ? draft.customerInstructions.trim()
        : "";

    if (instructions) {
      box.appendChild(
        detailRow(
          "Lisätiedot työn suorittajalle",
          instructions
        )
      );
    }

    box.appendChild(
      element(
        "p",
        "helpster-cart-details__notice",
        "Palvelun lopullista hintaa ei ole vielä " +
          "vahvistettu. Tietojen muokkaus ei " +
          "päivitä ostoskorin hintaa."
      )
    );

    target.replaceChildren(box);
  }

  function createEditForm({
    draft,
    onSave,
    onCancel,
  }) {
    const form = element(
      "form",
      "helpster-cart-details__form"
    );

    const fields =
      draft.editSchema?.fields || [];

    if (
      !Array.isArray(fields) ||
      fields.length === 0
    ) {
      throw new Error(
        "Palvelun muokkauskenttiä ei löytynyt."
      );
    }

    const controls = new Map();

    for (const field of fields) {
      const wrapper = element(
        "div",
        "helpster-cart-details__field"
      );

      const id =
        "helpster-edit-" +
        draft.draftId + "-" + field.key;

      const label = element(
        "label",
        "helpster-cart-details__field-label",
        field.label +
          (field.unit ? ` (${field.unit})` : "")
      );

      label.htmlFor = id;

      let control;

      if (field.type === "integer") {
        control = element(
          "input",
          "helpster-cart-details__input"
        );

        control.type = "number";
        control.step = "1";

        if (Number.isInteger(field.min)) {
          control.min = String(field.min);
        }

        if (Number.isInteger(field.max)) {
          control.max = String(field.max);
        }

        const current =
          draft.customerAnswers?.[field.key];

        control.value =
          current === null ||
          current === undefined
            ? ""
            : String(current);

      } else if (field.type === "choice") {
        control = element(
          "select",
          "helpster-cart-details__input"
        );

        const placeholder =
          document.createElement("option");

        placeholder.value = "";
        placeholder.textContent =
          "Valitse vaihtoehto";
        placeholder.disabled = true;

        control.appendChild(placeholder);

        for (const option of field.options || []) {
          const opt =
            document.createElement("option");

          opt.value = option.value;
          opt.textContent = option.label;

          control.appendChild(opt);
        }

        control.value = String(
          draft.customerAnswers?.[field.key] ?? ""
        );

      } else {
        continue;
      }

      control.id = id;
      control.required = true;
      control.name = field.key;

      controls.set(field.key, {
        field,
        control,
      });

      wrapper.append(label, control);
      form.appendChild(wrapper);
    }

    if (controls.size === 0) {
      throw new Error(
        "Muokattavia kenttiä ei löytynyt."
      );
    }

    const instructionsWrapper = element(
      "div",
      "helpster-cart-details__field"
    );

    const instructionsId =
      "helpster-instructions-" + draft.draftId;

    const instructionsLabel = element(
      "label",
      "helpster-cart-details__field-label",
      "Lisäohjeet työn suorittajalle"
    );

    instructionsLabel.htmlFor = instructionsId;

    const instructionsInput = element(
      "textarea",
      "helpster-cart-details__input"
    );

    instructionsInput.id = instructionsId;
    instructionsInput.name = "customerInstructions";
    instructionsInput.rows = 4;
    instructionsInput.maxLength = 2000;

    instructionsInput.placeholder =
      "Voit kertoa esimerkiksi erityistoiveista " +
      "tai työn suorittamista helpottavista asioista.";

    instructionsInput.value =
      typeof draft.customerInstructions === "string"
        ? draft.customerInstructions
        : "";

    const instructionsHint = element(
      "p",
      "helpster-cart-details__hint",
      "Vapaaehtoinen kenttä, enintään 2000 merkkiä."
    );

    instructionsWrapper.append(
      instructionsLabel,
      instructionsInput,
      instructionsHint
    );

    form.appendChild(instructionsWrapper);

    const feedback = element(
      "p",
      "helpster-cart-details__form-feedback"
    );

    feedback.setAttribute("role", "status");
    feedback.setAttribute("aria-live", "polite");

    const actions = element(
      "div",
      "helpster-cart-details__actions"
    );

    const saveButton = element(
      "button",
      "helpster-cart-details__save",
      "Tallenna muutokset"
    );

    saveButton.type = "submit";

    const cancelButton = element(
      "button",
      "helpster-cart-details__cancel",
      "Peruuta"
    );

    cancelButton.type = "button";
    cancelButton.addEventListener(
      "click",
      onCancel
    );

    actions.append(
      saveButton,
      cancelButton
    );

    form.append(actions, feedback);

    form.addEventListener(
      "submit",
      async event => {
        event.preventDefault();

        if (!form.reportValidity()) return;

        const changes = {};

        for (const [key, entry] of controls) {
          const { field, control } = entry;

          const current =
            draft.customerAnswers?.[key];

          const next =
            field.type === "integer"
              ? Number(control.value)
              : control.value;

          if (next !== current) {
            changes[key] = next;
          }
        }

        const previousInstructions =
          typeof draft.customerInstructions === "string"
            ? draft.customerInstructions.trim()
            : "";

        const nextInstructions =
          instructionsInput.value.trim();

        if (nextInstructions !== previousInstructions) {
          changes.customerInstructions =
            nextInstructions;
        }

        if (Object.keys(changes).length === 0) {
          feedback.textContent =
            "Et muuttanut palvelun tietoja.";
          return;
        }

        saveButton.disabled = true;
        cancelButton.disabled = true;
        feedback.textContent =
          "Tallennetaan muutoksia...";

        try {
          await onSave(changes);
        } catch (error) {
          feedback.textContent =
            error?.message ||
            "Tallentaminen epäonnistui.";

          if (error?.code === "revision_conflict") {
            feedback.textContent =
              "Palvelun tietoja on muutettu muualla. " +
              "Sulje muokkaus ja avaa se uudelleen.";
          }
        } finally {
          saveButton.disabled = false;
          cancelButton.disabled = false;
        }
      }
    );

    return form;
  }

  function createServiceCard(item) {
    const draftId =
      item.properties._helpster_draft_id;

    const card = element(
      "article",
      "helpster-cart-details__card"
    );

    const title = element(
      "h3",
      "helpster-cart-details__title",
      item.product_title || "Palvelu"
    );

    const toggle = element(
      "button",
      "helpster-cart-details__toggle",
      "Näytä palvelun tiedot"
    );

    toggle.type = "button";
    toggle.setAttribute(
      "aria-expanded",
      "false"
    );

    const body = element(
      "div",
      "helpster-cart-details__body"
    );

    body.hidden = true;

    card.append(title, toggle, body);

    let currentDraft = null;
    let busy = false;

    function renderLoadedDraft() {
      const summary = element("div");

      renderSummary(summary, currentDraft);

      const editButton = element(
        "button",
        "helpster-cart-details__edit",
        "Muokkaa tietoja"
      );

      editButton.type = "button";

      editButton.addEventListener(
        "click",
        async () => {
          if (busy) return;

          busy = true;
          editButton.disabled = true;

          try {
            // Haetaan aina tuore versio
            // ennen muokkauslomakkeen avaamista.
            currentDraft = await getDraft(draftId);

            const form = createEditForm({
              draft: currentDraft,

              onCancel: () => {
                renderLoadedDraft();
              },

              onSave: async changes => {

                await updateDraft({
                  draftId,
                  expectedRevision:
                    currentDraft.revision,
                  changes,
                });

                await syncHelpsterCartPrice(draftId);

                window.location.reload();
              },
            });

            body.replaceChildren(form);

          } catch (error) {
            renderLoadedDraft();

            const message = element(
              "p",
              "helpster-cart-details__error",
              error?.message ||
              "Muokkausta ei voitu avata."
            );

            body.prepend(message);

          } finally {
            busy = false;
            editButton.disabled = false;
          }
        }
      );

      const refreshPriceButton = element(
        "button",
        "helpster-cart-details__edit",
        "Tarkista / päivitä palvelun hinta"
      );

      refreshPriceButton.type = "button";

      refreshPriceButton.addEventListener(
        "click",
        async () => {
          if (busy) return;

          busy = true;
          refreshPriceButton.disabled = true;
          editButton.disabled = true;

          const feedback = element(
            "p",
            "helpster-cart-details__notice",
            "Tarkistetaan palvelun hintaa..."
          );

          body.appendChild(feedback);

          try {
            await syncHelpsterCartPrice(draftId);

            window.location.reload();

          } catch (error) {
            feedback.className =
              "helpster-cart-details__error";

            feedback.textContent =
              error?.message ||
              "Palvelun hinnan päivittäminen epäonnistui.";

          } finally {
            busy = false;
            refreshPriceButton.disabled = false;
            editButton.disabled = false;
          }
        }
      );

      body.replaceChildren(
        summary,
        editButton,
        refreshPriceButton
      );
    }

    toggle.addEventListener(
      "click",
      async () => {
        if (busy) return;

        if (!body.hidden) {
          body.hidden = true;
          toggle.textContent =
            "Näytä palvelun tiedot";
          toggle.setAttribute(
            "aria-expanded",
            "false"
          );
          return;
        }

        body.hidden = false;
        toggle.textContent =
          "Piilota palvelun tiedot";
        toggle.setAttribute(
          "aria-expanded",
          "true"
        );

        busy = true;
        toggle.disabled = true;

        showMessage(
          body,
          "Haetaan palvelutietoja..."
        );

        try {
          currentDraft = await getDraft(draftId);
          renderLoadedDraft();

        } catch (error) {
          showMessage(
            body,
            error?.message ||
              "Palvelutietoja ei voitu hakea.",
            true
          );

        } finally {
          busy = false;
          toggle.disabled = false;
        }
        try {
          const [quote, cart] = await Promise.all([
            getHelpsterDraftQuote(draftId),
            readHelpsterCart(),
          ]);

          const line =
            findHelpsterDraftLine(cart, draftId);

          if (
            String(line.variant_id) !==
              String(quote.shopify.variantId) ||
            line.price !== quote.finalPriceCents
          ) {
            const warning = element(
              "p",
              "helpster-cart-details__error",
              "Palvelun tietoja tai hintaa on muutettu. " +
              "Paina 'Tarkista / päivitä palvelun hinta' " +
              "ennen tilauksen jatkamista."
            );

            body.prepend(warning);
          }

        } catch (error) {
          const warning = element(
            "p",
            "helpster-cart-details__error",
            "Palvelun hintaa ei voitu vahvistaa: " +
              (error?.message || "tuntematon virhe")
          );

          body.prepend(warning);
        }
      }
    );

    return card;
  }

  async function initialize() {
    try {
      const response = await fetch(
        shopRoot + "cart.js",
        {
          credentials: "same-origin",
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          "Ostoskorin lataaminen epäonnistui."
        );
      }

      const cart = await response.json();

      const services = cart.items.filter(
        item =>
          item.properties?._helpster_service_key ===
            "general_cleaning" &&
          typeof item.properties?._helpster_draft_id ===
            "string" &&
          item.properties._helpster_draft_id
      );

      if (services.length === 0) {
        root.hidden = true;
        return;
      }

      content.replaceChildren();

      for (const item of services) {
        content.appendChild(
          createServiceCard(item)
        );
      }

    } catch (error) {
      showMessage(
        content,
        error?.message ||
          "Palvelutietoja ei voitu ladata.",
        true
      );
    }
  }

  initialize();
})();